import { supabase } from "../supabaseClient";

// --- localStorage helpers ---
const LS_KEY = "be_data";
export function lsLoad() {
  try { return JSON.parse(localStorage.getItem(LS_KEY)) || {}; } catch { return {}; }
}
export function lsSave(data) {
  localStorage.setItem(LS_KEY, JSON.stringify(data));
}

// --- Auth headers helper ---
async function getAuthHeaders() {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) return null;
  return { Authorization: `Bearer ${token}` };
}

async function authFetch(url, options = {}) {
  const authHeaders = await getAuthHeaders();
  if (!authHeaders) return null; // Not logged in → caller should fallback
  const res = await fetch(url, {
    ...options,
    headers: { ...options.headers, ...authHeaders },
  });
  if (res.status === 401) return null; // Token expired → fallback
  return res;
}

// --- API calls ---
export async function fetchTodayChallenges(date) {
  const res = await authFetch(`/api/challenges?date=${date}`);
  if (!res || !res.ok) return [];
  const data = await res.json();
  return Array.isArray(data) ? data : data ? [data] : [];
}

export async function fetchHistory(year) {
  const res = await authFetch(`/api/challenges?history=true&year=${year}`);
  if (!res || !res.ok) return [];
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

export async function fetchStreak() {
  const res = await authFetch("/api/streak");
  if (!res || !res.ok) return null;
  const data = await res.json();
  return data && !data.error ? data : null;
}

export async function saveChallengeAPI(record) {
  const res = await authFetch("/api/challenges", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(record),
  });
  if (!res) return null;
  return res.json();
}

export async function updateChallengeAPI(id, updates) {
  const res = await authFetch(`/api/challenges?id=${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updates),
  });
  if (!res) return null;
  return res.json();
}

export async function updateStreakAPI(dateStr) {
  const res = await authFetch("/api/streak", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ date: dateStr }),
  });
  if (!res) return null;
  return res.json();
}

// --- AI calls (via backend proxy) ---
export async function generateChallengeAI(category, dateStr) {
  const res = await authFetch("/api/generate-challenge", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      categoryLabel: category.label,
      categoryEmoji: category.emoji,
      dateStr,
    }),
  });
  if (!res || !res.ok) throw new Error("AI generation failed");
  return res.json();
}

export async function generateReflectionAI(title, reflectionText) {
  const res = await authFetch("/api/generate-reflection", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ title, reflectionText }),
  });
  if (!res || !res.ok) throw new Error("AI reflection failed");
  const data = await res.json();
  return data.feedback || "";
}

// --- Feedback ---
export async function fetchFeedbackList() {
  const res = await authFetch("/api/feedback");
  if (!res || !res.ok) return [];
  return res.json();
}

export async function submitFeedback(type, message) {
  const res = await authFetch("/api/feedback", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ type, message }),
  });
  if (!res) return null;
  return res.json();
}

// --- Notifications ---
export async function fetchNotifications() {
  const res = await authFetch("/api/notifications");
  if (!res || !res.ok) return [];
  return res.json();
}

export async function markNotificationRead(id) {
  const res = await authFetch(`/api/notifications?id=${id}`, { method: "PATCH" });
  if (!res || !res.ok) return null;
  return res.json();
}

export async function markAllNotificationsRead() {
  const res = await authFetch("/api/notifications?mark_all=1", { method: "PATCH" });
  if (!res || !res.ok) return null;
  return res.json();
}

// --- Admin users ---
export async function fetchAdminUsers() {
  const res = await authFetch("/api/admin-users");
  if (!res) return null;
  if (res.status === 403) return "forbidden";
  if (!res.ok) return null;
  return res.json();
}

// --- Admin feedback ---
export async function fetchAdminFeedback() {
  const res = await authFetch("/api/admin-feedback");
  if (!res) return null;
  if (res.status === 403) return "forbidden";
  if (!res.ok) return null;
  return res.json();
}

export async function updateAdminFeedback(id, updates) {
  const res = await authFetch(`/api/admin-feedback?id=${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updates),
  });
  if (!res || !res.ok) return null;
  return res.json();
}

export async function fetchAdminSupport() {
  const res = await authFetch("/api/admin-feedback?resource=support");
  if (!res) return null;
  if (res.status === 403) return "forbidden";
  if (!res.ok) return null;
  return res.json();
}

export async function updateAdminSupport(id, updates) {
  const res = await authFetch(`/api/admin-feedback?resource=support&id=${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updates),
  });
  if (!res || !res.ok) return null;
  return res.json();
}

// --- Community stats ---
export async function fetchCommunityStats() {
  const res = await authFetch("/api/community");
  if (!res || !res.ok) return null;
  const data = await res.json();
  return data && !data.error ? data : null;
}

// --- Migration ---
export async function migrateLocalData() {
  const data = lsLoad();
  const dates = Object.keys(data).filter(k => k !== "_streak" && k.match(/^\d{4}-\d{2}-\d{2}$/));
  if (dates.length === 0) return;

  for (const date of dates) {
    try {
      await authFetch("/api/challenges", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data[date]),
      });
    } catch (e) { console.error("Lỗi migrate:", date, e); }
  }

  if (data._streak) {
    try {
      await authFetch("/api/streak", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
    } catch (e) { console.error("Lỗi migrate streak:", e); }
  }

  localStorage.removeItem(LS_KEY);
}

// --- Auth ---
export async function loginWithGoogle() {
  return supabase.auth.signInWithOAuth({
    provider: "google",
    options: { redirectTo: `${window.location.origin}/app` },
  });
}

export async function logout() {
  return supabase.auth.signOut();
}

// --- Helper: build challenge record ---
export function buildChallengeRecord(challengeObj, selectedCat, todayStr, completed, reflectionText, aiFeedback) {
  return {
    date: todayStr,
    title: challengeObj.title,
    task: challengeObj.task,
    why: challengeObj.why,
    tip: challengeObj.tip,
    time_required: challengeObj.time,
    category_id: selectedCat.id,
    category_label: selectedCat.label,
    category_emoji: selectedCat.emoji,
    completed,
    reflection: reflectionText,
    ai_feedback: aiFeedback,
  };
}

// --- Load from localStorage (fallback) ---
export function loadFromLocalStorage(todayStr) {
  const data = lsLoad();
  const todayData = data[todayStr] || null;
  const streakData = data._streak || { current_streak: 0, longest_streak: 0 };
  const hist = Object.entries(data)
    .filter(([k]) => k !== "_streak" && k.match(/^\d{4}-\d{2}-\d{2}$/))
    .map(([, v]) => v)
    .sort((a, b) => b.date.localeCompare(a.date));
  return { todayData, streakData, history: hist };
}
